'use client'

/**
 * The live draft panel's open state, shared (#15224).
 *
 * The recording bar renders the ONE live draft panel
 * (`./recording-bar-live-panel.tsx`, #15223). Two buttons open it: the bar's
 * own status cluster (`./recording-bar-status.tsx`, #15576), and the note
 * page's live recording indicator
 * (`@/components/notes/note-live-recording-indicator.tsx`, #15224). Both
 * read and write this store, so they agree on whether it is open — and there
 * is never a second copy of the panel.
 *
 * It holds four facts:
 *
 *   - `openFor` — the recording the panel is open for, or null. Keyed by
 *     recording, so a new capture (or Stop) never inherits an open panel;
 *   - `returnFocusTo` — the button that opened it, where focus goes back on
 *     Escape or the X (the bar's button, or the note page's indicator);
 *   - `availableFor` — the recording whose panel the bar can show, published
 *     by the bar's panel once the owner-only `getLiveSummary` answered it
 *     (`useLiveSummary`'s `available`). The note page offers the button only
 *     for that recording: the SAME gate, and never a button that would open
 *     nothing because no bar in this tab is holding the panel;
 *   - `hasUpdateFor` — the recording whose live draft has a version the
 *     viewer has not seen, published by the same panel (#15576), so the
 *     bar's status button can carry the update dot the panel computes.
 *
 * Module-level and framework-free underneath, read through
 * `useSyncExternalStore`, as `@/lib/notetaker/recording-session-store` is.
 */

import { useEffect, useSyncExternalStore } from 'react'

/** The panel's element id, for the openers' `aria-controls`. */
export const LIVE_DRAFT_PANEL_ID = 'live-draft-panel'

/**
 * The bar's status button's id (#15576): where focus goes on close when the
 * button that opened the panel has left the page.
 */
export const LIVE_DRAFT_BAR_TOGGLE_ID = 'live-draft-bar-toggle'

interface LiveDraftPanelState {
  openFor: string | null
  availableFor: string | null
  hasUpdateFor: string | null
}

const CLOSED: LiveDraftPanelState = {
  openFor: null,
  availableFor: null,
  hasUpdateFor: null,
}

let state: LiveDraftPanelState = CLOSED
let returnFocusTo: HTMLElement | null = null
const listeners = new Set<() => void>()

function publish(next: LiveDraftPanelState) {
  if (
    next.openFor === state.openFor &&
    next.availableFor === state.availableFor &&
    next.hasUpdateFor === state.hasUpdateFor
  ) {
    return
  }
  state = next
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function getSnapshot(): LiveDraftPanelState {
  return state
}

function getServerSnapshot(): LiveDraftPanelState {
  return CLOSED
}

/** Open the panel for `recordingId`; focus returns to `opener` on close. */
export function openLiveDraftPanel(
  recordingId: string,
  opener: HTMLElement | null,
): void {
  returnFocusTo = opener
  publish({ ...state, openFor: recordingId })
}

/**
 * Close the panel. Returns the button that opened it — the caller moves
 * focus there, when it is still on the page.
 */
export function closeLiveDraftPanel(): HTMLElement | null {
  const opener = returnFocusTo
  returnFocusTo = null
  publish({ ...state, openFor: null })
  return opener
}

/** Close the panel only if it is open for `recordingId`. */
export function closeLiveDraftPanelFor(recordingId: string | null): void {
  if (recordingId === null || state.openFor !== recordingId) return
  closeLiveDraftPanel()
}

/** The bar's panel can show `recordingId`'s live draft (null: none). */
export function setLiveDraftPanelAvailable(recordingId: string | null): void {
  publish({ ...state, availableFor: recordingId })
}

/** Withdraw `recordingId`'s availability, if it is still the one published. */
export function clearLiveDraftPanelAvailable(recordingId: string): void {
  if (state.availableFor !== recordingId) return
  publish({ ...state, availableFor: null })
}

/** `recordingId`'s live draft has an unseen version. */
export function setLiveDraftPanelHasUpdate(recordingId: string): void {
  publish({ ...state, hasUpdateFor: recordingId })
}

/** Withdraw `recordingId`'s update, if it is still the one published. */
export function clearLiveDraftPanelHasUpdate(recordingId: string): void {
  if (state.hasUpdateFor !== recordingId) return
  publish({ ...state, hasUpdateFor: null })
}

/** Back to closed and unavailable. Tests only. */
export function resetLiveDraftPanelStore(): void {
  returnFocusTo = null
  publish(CLOSED)
}

/** Whether the panel is open for this recording. */
export function useLiveDraftPanelOpen(recordingId: string | null): boolean {
  const { openFor } = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerSnapshot,
  )
  return recordingId !== null && openFor === recordingId
}

/** Whether the bar's panel can show this recording's live draft. */
export function useLiveDraftPanelAvailable(
  recordingId: string | null,
): boolean {
  const { availableFor } = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerSnapshot,
  )
  return recordingId !== null && availableFor === recordingId
}

/** Whether this recording's live draft has a version not yet seen. */
export function useLiveDraftPanelHasUpdate(
  recordingId: string | null,
): boolean {
  const { hasUpdateFor } = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerSnapshot,
  )
  return recordingId !== null && hasUpdateFor === recordingId
}

/**
 * The host's half (#15224): an open asked for while the bar is FOLDED — from
 * the note page's indicator — unfolds the bar, since the panel belongs to
 * the expanded bar. `setCollapsed` is the host's state setter.
 */
export function useUnfoldForLivePanel(
  recordingId: string | null,
  setCollapsed: (collapsed: boolean) => void,
): void {
  const open = useLiveDraftPanelOpen(recordingId)
  useEffect(() => {
    if (open) setCollapsed(false)
  }, [open, setCollapsed])
}
